/**
 * useSessionTimeout — tracks user inactivity and exposes the state required
 * to display a "your session will expire in X" warning modal.
 *
 * - Resets on any user activity: mousemove, mousedown, keydown, touchstart,
 *   scroll, click. Throttled to once per second to avoid layout churn.
 * - Auto-logs out when the total inactivity duration is reached.
 * - Cross-tab aware: any activity in another Realty237 tab broadcasts a
 *   `realty237:activity` BroadcastChannel ping that resets the timer here.
 *
 * Defaults (per UX-15 spec):
 *   totalMs   = 30 min  (full inactivity allowed before logout)
 *   warningMs =  2 min  (modal shows when only this much remains)
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click',
];
const ACTIVITY_THROTTLE_MS = 1000;
const TICK_MS = 1000;
const CHANNEL_NAME = 'realty237:activity';

export const useSessionTimeout = ({
  enabled = true,
  totalMs = 30 * 60 * 1000,
  warningMs = 2 * 60 * 1000,
  onTimeout,
} = {}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingMs, setRemainingMs] = useState(totalMs);
  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(0);
  const channelRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  // Public API — call this when the user clicks "Rester connecté"
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setRemainingMs(totalMs);
    setShowWarning(false);
  }, [totalMs]);

  useEffect(() => {
    if (!enabled) {
      setShowWarning(false);
      return undefined;
    }

    // Cross-tab activity broadcaster
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (e) => {
        if (e?.data?.type === 'ping') {
          lastActivityRef.current = Date.now();
        }
      };
    } catch (_) {
      channelRef.current = null;
    }

    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // Throttled broadcast — at most once per second
      if (now - lastBroadcastRef.current > ACTIVITY_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        try { channelRef.current?.postMessage({ type: 'ping' }); } catch (_) { /* noop */ }
      }
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    // Tick every second to evaluate the remaining window
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = totalMs - elapsed;
      setRemainingMs(Math.max(0, remaining));
      if (remaining <= 0) {
        // Session expired — fire callback once
        setShowWarning(false);
        if (onTimeoutRef.current) {
          try { onTimeoutRef.current(); } catch (_) { /* noop */ }
        }
      } else if (remaining <= warningMs) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }, TICK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.clearInterval(tick);
      try { channelRef.current?.close(); } catch (_) { /* noop */ }
      channelRef.current = null;
    };
  }, [enabled, totalMs, warningMs]);

  return { showWarning, remainingMs, resetTimer };
};

export default useSessionTimeout;
